import React from "react";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { IconButton } from "@mui/material";
import useFinanceInventory from "../data-handling/use-finance-inventory";
import {
  INVENTORY_ACTIVITY_NESTED_ROUTES,
  INVENTORY_ACTIVITY_TYPES,
  ITEMS_PER_PAGE,
  TABLE_HEAD_KEYS,
} from "../data-handling/finance-inventory-constants";
import { IActivityItem } from "../data-handling/finance-inventory-types";
import moment from "moment";
import { DATE_TIME_FORMATS } from "../../../data/constants";
import no_data_found from "./../../../assets/images/no-data-found-2.png";
import { useNavigate } from "react-router-dom";
import { getStatusStageComponent, isGRN } from "../utils/common";
import { currency } from "../../../helpers/common-helper";

const currencySymbol = currency();
function TableList() {
  const {
    currentActivityList,
    onPageChange,
    pageNumber,
    activityType,
    setSelectedActivityRow,
    activityListsByPage,
    branchIds,
  } = useFinanceInventory();

  const getVisibleColumns = (): string[] => {
    const baseColumns = [
      TABLE_HEAD_KEYS.date,
      TABLE_HEAD_KEYS.branchInfo,
      TABLE_HEAD_KEYS.activity,
      TABLE_HEAD_KEYS.billAmount,
      TABLE_HEAD_KEYS.orderId,
      TABLE_HEAD_KEYS.status,
      TABLE_HEAD_KEYS.supplierName,
      TABLE_HEAD_KEYS.taxAmount,
      TABLE_HEAD_KEYS.supplierInvoiceNumber,  
    ];

    if (activityType === INVENTORY_ACTIVITY_TYPES.post_final_goods_received_note) {
      baseColumns.push(TABLE_HEAD_KEYS.isEdited);
    }

    return baseColumns;
  };


  const dataAvailable =
    Object.keys(activityListsByPage || {}).length !== 0
  const pageDataAvailable = currentActivityList?.length !== 0;
  const navigate = useNavigate();

  const getHeadingAndCellValue = (type: string, row?: IActivityItem) => {
    switch (type) {
      case TABLE_HEAD_KEYS.date:
        return {
          heading: "DATE",
          cellValue: moment(row?.activityDate).format(
          DATE_TIME_FORMATS.dayMonthYearShort
        ),
        };
      case TABLE_HEAD_KEYS.activity:
        return {
          heading: isGRN(activityType) ? "GOODS RECEIVED NOTE#" : "TRANSFER IN#",
          cellValue: row?.orderNumber,
        };
      case TABLE_HEAD_KEYS.billAmount:
        return {
          heading: "BILL AMOUNT",
          cellValue: `${currencySymbol}${row?.netCost}`,
        }
      case TABLE_HEAD_KEYS.orderId:
        return {
          heading: isGRN(activityType) ? "PURCHASE ORDER#" : "INDENT OUT#",
          cellValue: row?.sourceOrderNumber,
        }
      case TABLE_HEAD_KEYS.status:
        return {
          heading: "Approval Stage",
          cellValue: getStatusStageComponent(row)
        }
      case TABLE_HEAD_KEYS.isEdited:
        return {
          heading: "Is Edited",
          cellValue: row?.isPostFinalActivityEdited ? "Yes" : "No"
        };
      case TABLE_HEAD_KEYS.supplierName:
        return {
          heading: "SUPPLIER NAME",
          cellValue: row?.supplierName,
        }
      case TABLE_HEAD_KEYS.branchInfo:{
        const branchName = isGRN(activityType) ? row?.sourceBranchName : row?.targetBranchName;
        const branchCode = isGRN(activityType) ? row?.sourceBranchCode : row?.targetBranchCode;
        return {
          heading: "BRANCH INFO",
          cellValue: (
            <div className="flex flex-col max-w-[180px]">
              <span 
                className="font-400 overflow-hidden text-ellipsis whitespace-nowrap"
                title={branchName || ""}
              >
                {branchName || "--"}
              </span>
              <span 
                className="text-xs text-[#7F7F7F] mt-2 overflow-hidden text-ellipsis whitespace-nowrap"
                title={branchCode || ""}
              >
                {branchCode || "--"}
              </span>
            </div>
          ),
        };
      }    
      case TABLE_HEAD_KEYS.taxAmount:
        return {
          heading: "TAX AMOUNT",
          cellValue: `${currencySymbol}${row?.totalTax}`,
        }
      case TABLE_HEAD_KEYS.supplierInvoiceNumber:
        return {
          heading: "SUPPLIER INVOICE",
          cellValue: row?.supplierInvoiceNumber,
        }  
      default:
        return {
          heading: "--",
          cellValue: "--",
        }
    }
  }

  const getEmptyMessageFromActivity = () => {
    switch (activityType) {
      case INVENTORY_ACTIVITY_TYPES.goods_received_note:
        return "No Goods Received Note!";
      case INVENTORY_ACTIVITY_TYPES.transfer_ins:
        return "No Transfer In";
      default:
        return "No results!";
    }
  };

  return (
    <div className="flex flex-col justify-between h-full">
      <div className="flex-1 overflow-x-auto"> 
      <table className="min-w-full table-auto">
        <thead className="text-sm text-[#7F7F7F]">
          <tr className="bg-[#F6F6F6]">
            {getVisibleColumns().map((columnName, idx) => (
              <th
                className={`p-12 font-500 text-xs text-left whitespace-nowrap ${
                  idx === 0 ? "pl-24" : ""
                } ${idx === getVisibleColumns()?.length - 1 ? "pr-24" : ""}`}
              >
                {getHeadingAndCellValue(columnName)?.heading ?? "--"}
              </th>
            ))}
          </tr>
        </thead>
        {pageDataAvailable && (
          <tbody className="text-sm">
            {currentActivityList?.map((row) => {
              return (
                <tr
                  key={row.orderNumber}
                  onClick={() => {
                    navigate(INVENTORY_ACTIVITY_NESTED_ROUTES.activityEdit);
                    setSelectedActivityRow(row);
                  }}
                  className="border-b hover:bg-gray-50 cursor-pointer"
                >
                  {getVisibleColumns().map((columnName, index) => {
                    const cellValue = getHeadingAndCellValue(columnName, row)?.cellValue ?? "";

                    return (
                      <td
                        className={`p-12 font-400 text-[0.813rem] whitespace-nowrap ${
                          index === 0 ? "pl-24" : ""
                        }`}
                      >
                        {cellValue || "--"}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        )}
      </table>
      {branchIds.length === 0 ? (
        <div className="flex-1 justify-center items-center flex flex-col h-full">
          <img className="h-[50px] w-[50px]" src={no_data_found} />
          <div className="text-14 mt-14 text-[#000000B2]">
            No Stores Selected
          </div>
        </div>
      ) : (
        !pageDataAvailable && (
          <div className="flex-1 justify-center items-center flex flex-col">
            <img className="h-[50px] w-[50px]" src={no_data_found} />
            <div className="text-14 mt-14 text-[#000000B2]">
              {getEmptyMessageFromActivity()}
            </div>
          </div>
        )
      )}
      </div>
      {dataAvailable && (
        <div className="flex justify-end items-center mb-16 mt-24 pr-20">
          <span className="text-[13px] mr-16 font-400">{`${
            pageNumber === 1 ? pageNumber : ITEMS_PER_PAGE * (pageNumber - 1 )
          } - ${ITEMS_PER_PAGE * (pageNumber - 1 ) + ITEMS_PER_PAGE}`}</span>
          <IconButton onClick={() => onPageChange("prev")}>
            <KeyboardArrowLeftIcon className="h-24 w-24 text-[#898989]" />
          </IconButton>
          <IconButton onClick={() => onPageChange("next")}>
            <KeyboardArrowRightIcon className="h-24 w-24 text-[#898989]" />
          </IconButton>
        </div>
      )}
    </div>
  );
}

export default TableList;
